import { REQUIRED_MESSAGE, requiredString } from '@wyz/utils';
import type { TFunction } from '@wyz/types';
import { z } from 'zod';
import { TranslationKeys } from '~/common/types';

export const S2S_FLOW_TYPES = [
  'INQUIRY',
  'ORDER',
  'TYRES_ORDER',
  'SEARCH_QUOTES',
] as const;
export const S2S_ACTIVE_STATUSES = ['ACTIVE', 'INACTIVE'] as const;

export const s2sFlowFormSchema = z.object({
  type: z.enum(S2S_FLOW_TYPES),
  partner: z.string().nullable().optional(),
  status: z.enum(S2S_ACTIVE_STATUSES),
  url: z.string().nullable().optional(),
});

export const s2sBusinessRuleFormEntrySchema = z.object({
  id: z.string(),
  parameters: z.record(z.string(), z.string()),
});

export const s2sFlowTemplateFormSchema = z.object({
  name: requiredString,
  criteria: z
    .array(
      z.object({
        criterion: z.object({ id: z.string() }),
        values: z.array(requiredString).min(1, { message: REQUIRED_MESSAGE }),
      }),
    )
    .min(1, { message: REQUIRED_MESSAGE }),
  flows: z.array(s2sFlowFormSchema),
  businessRules: z.array(s2sBusinessRuleFormEntrySchema),
  newFlowType: z.string().optional(),
  newFlowPartner: z.string().optional(),
});

const s2sBusinessRuleLabelSchema = z.object({
  defaultValue: z.string(),
  i18nKey: z.custom<keyof TranslationKeys>(),
});

const s2sBusinessRuleParameterDefinitionSchema = z.object({
  key: z.string(),
  i18nKey: z.custom<keyof TranslationKeys>(),
  type: z.enum(['string', 'int', 'float', 'bool']),
  defaultValue: z.string().nullable(),
  constraints: z.array(
    z.object({
      type: z.string(),
      options: z.record(z.string(), z.unknown()),
    }),
  ),
});

export type S2SBusinessRuleParameterDefinition = z.infer<
  typeof s2sBusinessRuleParameterDefinitionSchema
>;

function castS2SBusinessRuleParameterValue(
  rawValue: string,
  type: string,
): string | number | boolean {
  switch (type) {
    case 'int': {
      const parsed = parseInt(rawValue, 10);
      return Number.isNaN(parsed) ? 0 : parsed;
    }
    case 'float': {
      const parsed = parseFloat(rawValue);
      return Number.isNaN(parsed) ? 0 : parsed;
    }
    default:
      return rawValue;
  }
}

function toConstraintNumber(value: unknown): number {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

export type S2SBusinessRuleParameterValidationResult = {
  key: keyof TranslationKeys;
};

export function validateS2SBusinessRuleParameterValue(
  rawValue: string,
  definition: S2SBusinessRuleParameterDefinition,
): S2SBusinessRuleParameterValidationResult | undefined {
  const castValue = castS2SBusinessRuleParameterValue(
    rawValue,
    definition.type,
  );

  for (const constraint of definition.constraints) {
    switch (constraint.type) {
      case 'NOT_BLANK':
        if (castValue === '' || castValue === null || castValue === undefined) {
          return { key: REQUIRED_MESSAGE as keyof TranslationKeys };
        }
        break;
      case 'POSITIVE':
        if (!z.number().positive().safeParse(castValue).success) {
          return { key: 'app.common.validations.positive_number' };
        }
        break;
      case 'POSITIVE_OR_ZERO':
        if (!z.number().nonnegative().safeParse(castValue).success) {
          return {
            key: 'app.common.assert.err_value_should_be_either_positive_or_zero',
          };
        }
        break;
      case 'MIN': {
        const min = toConstraintNumber(constraint.options.value);
        if (!z.number().gte(min).safeParse(castValue).success) {
          return {
            key: 'app.common.assert.err_value_should_be_greater_than_or_equal_to',
          };
        }
        break;
      }
      case 'MAX': {
        const max = toConstraintNumber(constraint.options.value);
        if (!z.number().lte(max).safeParse(castValue).success) {
          return {
            key: 'app.common.assert.err_value_should_be_less_than_or_equal_to',
          };
        }
        break;
      }
      case 'RANGE': {
        const hasMin = constraint.options.min !== undefined;
        const hasMax = constraint.options.max !== undefined;
        let rangeSchema = z.number();
        if (hasMin) {
          rangeSchema = rangeSchema.gte(
            toConstraintNumber(constraint.options.min),
          );
        }
        if (hasMax) {
          rangeSchema = rangeSchema.lte(
            toConstraintNumber(constraint.options.max),
          );
        }
        if ((hasMin || hasMax) && !rangeSchema.safeParse(castValue).success) {
          if (hasMin && hasMax) {
            return { key: 'app.common.assert.err_value_should_be_between' };
          }
          return {
            key: hasMin
              ? 'app.common.assert.err_value_should_be_greater_than_or_equal_to'
              : 'app.common.assert.err_value_should_be_less_than_or_equal_to',
          };
        }
        break;
      }
      case 'CHOICE': {
        const choices = Array.isArray(constraint.options.choices)
          ? constraint.options.choices
          : [];
        const normalizedChoices = choices.map((choice) => String(choice));
        const isValidChoice =
          normalizedChoices.length > 0 &&
          z
            .enum(normalizedChoices as [string, ...string[]])
            .safeParse(String(castValue)).success;
        if (!isValidChoice) {
          return { key: 'app.common.assert.err_value_should_be_valid_choice' };
        }
        break;
      }
      default:
        break;
    }
  }

  return undefined;
}

export const s2sBusinessRuleSchema = z.object({
  id: z.string(),
  name: s2sBusinessRuleLabelSchema,
  description: s2sBusinessRuleLabelSchema,
  code: z.string(),
  parameterDefinitions: z.array(s2sBusinessRuleParameterDefinitionSchema),
});

export function buildS2sFlowTemplateFormSchema(
  businessRulesLookup: Record<string, z.infer<typeof s2sBusinessRuleSchema>>,
  t: TFunction<TranslationKeys>,
) {
  return s2sFlowTemplateFormSchema.superRefine((data, ctx) => {
    data.businessRules.forEach((entry, ruleIndex) => {
      const definition = businessRulesLookup[entry.id];
      if (!definition) return;
      Object.entries(entry.parameters).forEach(([key, value]) => {
        const paramDef = definition.parameterDefinitions.find(
          (d) => d.key === key,
        );
        if (!paramDef) return;
        const violation = validateS2SBusinessRuleParameterValue(
          value,
          paramDef,
        );
        if (violation) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(violation.key),
            path: ['businessRules', ruleIndex, 'parameters', key],
          });
        }
      });
    });
  });
}

const embeddedBusinessRuleSchema = z.object({
  rule: s2sBusinessRuleSchema,
  parameters: z.array(z.object({ key: z.string(), value: z.string() })),
});
const payloadEmbeddedBusinessRuleSchema = z.object({
  rule: z.object({ id: z.string() }),
  parameters: z.array(z.object({ key: z.string(), value: z.string() })),
});

export const s2sFlowTemplatePayloadSchema = s2sFlowTemplateFormSchema
  .omit({
    criteria: true,
    businessRules: true,
    newFlowType: true,
    newFlowPartner: true,
  })
  .extend({
    criteria: z.array(
      z.object({
        criterion: z.object({ id: z.string() }),
        values: z.array(
          z.object({
            id: z.string(),
            label: z.string(),
          }),
        ),
      }),
    ),
    businessRules: z.array(payloadEmbeddedBusinessRuleSchema),
  });

export const s2sFlowTemplateSchema = s2sFlowTemplatePayloadSchema
  .omit({
    businessRules: true,
  })
  .extend({
    id: z.string(),
    status: z.enum(S2S_ACTIVE_STATUSES),
    businessRules: z.array(embeddedBusinessRuleSchema),
  });
